import { ExportShipmentsStatus } from './export-shipments-status';
import { Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { tap } from 'rxjs/operators';

import { Address } from './address';
import { environment } from '../../environments/environment';
import { Logger } from '../global/logger';
import { CrownCompany } from './crown-company';
import { GetObjectResponse } from '../global/responses/get-object-response';
import { ShipmentSearchCriteria } from './shipment-search-criteria';
import { ExecuteObservable } from '../global/execute-observable';
import { SearchShipmentsInfo } from '../global/responses/search-shipments-info';
import { ErrorHandler } from '../global/error-handling/error-handler';

/**
 * This class is the service used to communicate Crown Connect data stored in CosmosDB and exported shipment data.
 */
@Injectable()
export class CrownConnectDataService {

// #region [ Fields ]

	/** The path to the quote controller. */
	private static readonly DataControllerPath: string = "Data/";

	/** The path to the shipment controller */
	private static readonly ShipmentControllerPath: string = "Shipment/";

	/** A behavior subject for the export status has changed */
	private exportStatusBehaviorSubject: BehaviorSubject<ExportShipmentsStatus | null> = new BehaviorSubject<ExportShipmentsStatus | null>(null);

// #region [ Constructor ]

	/**
	 * The constructor for CrownConnectDataService
	 * @param _HttpClient The HTTP client
	 * @param _ErrorHandler The error handler for showing toast messages
	 */
	constructor(
		private _HttpClient: HttpClient,
		private _ErrorHandler: ErrorHandler
	) { }

// #region [ Public Methods ]

	/**
	 * Fetches the saved addresses from the database.
	 *
	 * @returns A promise that supplies array of addresses.
	 */
	public GetAddresses(): Promise<Address[]> {
		let objObservable: Observable<GetObjectResponse<Address[]>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + CrownConnectDataService.DataControllerPath + "GetAddresses/";
		objObservable = this._HttpClient.get<GetObjectResponse<Address[]>>(strUrl, { withCredentials: true });

		// Setup logging.
		intIdentifier = Logger.ServiceStartLog("GET", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<Address[]>) => Logger.ServiceEndLog(intIdentifier, "GET", strUrl, objResponse)
		));

		return ExecuteObservable.ExecuteQuery<Address[]>(objObservable, "CrownConnectDataService.GetAddresses");
	}

	/**
	 * Fetches the Crown companies from the database.
	 *
	 * @returns A promise that supplies array of Crown companies.
	 */
	public GetCrownCompanies(): Promise<CrownCompany[]> {
		let objObservable: Observable<GetObjectResponse<CrownCompany[]>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + CrownConnectDataService.DataControllerPath + "GetCrownCompanies/";
		objObservable = this._HttpClient.get<GetObjectResponse<CrownCompany[]>>(strUrl, { withCredentials: true });

		// Setup logging.
		intIdentifier = Logger.ServiceStartLog("GET", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<CrownCompany[]>) => Logger.ServiceEndLog(intIdentifier, "GET", strUrl, objResponse)
		));

		return ExecuteObservable.ExecuteQuery<CrownCompany[]>(objObservable, "CrownConnectDataService.GetCrownCompanies");
	}

	/**
	 * Gets a count of shipments.
	 * @param Criteria The search criteria by which to filter the shipments.
	 */
	public GetShipmentsCount(Criteria: ShipmentSearchCriteria): Promise<number> {
		let objObservable: Observable<GetObjectResponse<number>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + CrownConnectDataService.ShipmentControllerPath + "GetShipmentsCount/";
		objObservable = this._HttpClient.post<GetObjectResponse<number>>(strUrl, Criteria, { withCredentials: true });

		// Setup logging.
		intIdentifier = Logger.ServiceStartLog("POST", strUrl, Criteria);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<any>) => Logger.ServiceEndLog(intIdentifier, "POST", strUrl, objResponse)
		));

		return ExecuteObservable.ExecuteQuery<number>(objObservable, "CrownConnectDataService.GetShipmentsCount");
	}

	/**
	 * Searches shipments based on the criteria supplied as a parameter.
	 *
	 * @param Criteria The shipment search criteria to use for searching.
	 * @returns A promise that supplies an array of shipments.
	 */
	public SearchShipments(Criteria: ShipmentSearchCriteria): Promise<SearchShipmentsInfo> {
		let objObservable: Observable<GetObjectResponse<SearchShipmentsInfo>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + CrownConnectDataService.ShipmentControllerPath + "SearchShipments/";
		objObservable = this._HttpClient.put<GetObjectResponse<SearchShipmentsInfo>>(strUrl, Criteria, { withCredentials: true });

		// Setup logging.
		intIdentifier = Logger.ServiceStartLog("PUT", strUrl, Criteria);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<any>) => Logger.ServiceEndLog(intIdentifier, "GET", strUrl, objResponse)
		));

		return ExecuteObservable.ExecuteQuery<SearchShipmentsInfo>(objObservable, "CrownConnectDataService.SearchShipments");
	}

	/**
	 * Sends a request to Dataflow to start exporting shipments
	 * @param criteria The shipment search criteria to use for exporting.
	 * @returns A promise that supplies the job ID from Dataflow.
	 */
	public ExportShipments(criteria: ShipmentSearchCriteria): Promise<string> {
		let objObservable: Observable<GetObjectResponse<string>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + CrownConnectDataService.ShipmentControllerPath + "export/";
		objObservable = this._HttpClient.post<GetObjectResponse<string>>(strUrl, criteria, { withCredentials: true });

		// Setup logging.
		intIdentifier = Logger.ServiceStartLog("POST", strUrl, criteria);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<any>) => Logger.ServiceEndLog(intIdentifier, "GET", strUrl, objResponse)
		));

		return ExecuteObservable.ExecuteQuery<string>(objObservable, "CrownConnectDataService.ExportShipments");
	}

	/**
	 * Gets the status of the shipments export job running in Dataflow
	 * @param jobId The job ID to check the status in Dataflow
	 * @returns A promise that supplies the export shipments status from Dataflow.
	 */
	public GetExportShipmentsStatus(jobId: string): Promise<ExportShipmentsStatus> {
		let objObservable: Observable<GetObjectResponse<ExportShipmentsStatus>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + CrownConnectDataService.ShipmentControllerPath + `${jobId}/export/`;
		objObservable = this._HttpClient.get<GetObjectResponse<ExportShipmentsStatus>>(strUrl, { withCredentials: true });

		// Setup logging.
		intIdentifier = Logger.ServiceStartLog("GET", strUrl, {jobId});
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<any>) => Logger.ServiceEndLog(intIdentifier, "GET", strUrl, objResponse)
		));

		return ExecuteObservable.ExecuteQuery<ExportShipmentsStatus>(objObservable, "CrownConnectDataService.GetExportShipmentsStatus");
	}

	/**
	 * Gets an observable for the export shipments status
	 * @returns An observable for the export shipments status
	 */
	public GetExportStatus(): Observable<ExportShipmentsStatus | null> {
		return this.exportStatusBehaviorSubject.asObservable();
	}

	/**
	 * Polls Dataflow on an interval to get the status or the export shipments job
	 * @param jobId The job ID to check the status in Dataflow
	 */
	public PollExportStatus(jobId: string): void {
		//Give an initial state to the status
		let initialStatus: ExportShipmentsStatus = {
			JobId: null,
			IsComplete: false,
			ProgressValue: 0,
			Error: null,
			ExportCsv: null
		};
		this.exportStatusBehaviorSubject.next(initialStatus);

		const pollingInterval: number = 3000
		let lastResponseDate = Date.now() + pollingInterval;
		let timer = setInterval(() => {
			let lastPoll = Date.now() - pollingInterval;
			if (lastResponseDate >= lastPoll) {
				this.GetExportShipmentsStatus(jobId)
				.then((status: ExportShipmentsStatus) => {
					lastResponseDate = Date.now();
					this.exportStatusBehaviorSubject.next(status);
					if (status && status.IsComplete) {
						clearInterval(timer);
						if (status.Error) {
							this._ErrorHandler.ShowError(null, "Could not export shipments; please try again.");
						} else {
							if (!status.ExportCsv || status.ExportCsv.length <= 0) {
								this._ErrorHandler.ShowInfo("Export Message", "There were no shipments to export.")
							} else {
								this._ErrorHandler.ShowInfo("Export Message", "Export Shipments complete.")
								this.DownloadExportCsv(status.ExportCsv);
							}
						}
					}
				});
			}
		}, pollingInterval);
	}

	/**
	 * Downloads the exported shipments CSV file
	 * @param exportCsv The base64 encoded string for the CSV file
	 */
	 private DownloadExportCsv(exportCsv: string): void {
		let csv: string = atob(exportCsv);
		const link: HTMLAnchorElement = document.createElement('a');
		link.href = URL.createObjectURL(new Blob([csv], {type : 'application/octet-stream;base64'}))
		link.download = 'ShipmentsExport.csv';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

}